import React from 'react';

/**
 * Side-by-side diff view showing user answer vs correct answer.
 * Color-codes differences and shows common mistake callouts.
 */
export default function FeedbackPanel({ result, acceptedAnswer, commonMistakes = [] }) {
  if (!result) return null;

  const { percentage, lineResults, matchedMistakes } = result;

  const statusColor = percentage === 100 ? 'green' : percentage >= 50 ? 'yellow' : 'red';
  const statusLabel = percentage === 100 ? 'Correct!' : percentage >= 50 ? 'Partial Credit' : 'Incorrect';

  return (
    <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
      {/* Status header */}
      <div className={`px-4 py-2 ${
        statusColor === 'green' ? 'bg-green-900/50 border-b border-green-800' :
        statusColor === 'yellow' ? 'bg-yellow-900/50 border-b border-yellow-800' :
        'bg-red-900/50 border-b border-red-800'
      }`}>
        <div className="flex items-center justify-between">
          <span className={`font-medium ${
            statusColor === 'green' ? 'text-green-400' :
            statusColor === 'yellow' ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {statusLabel} — {percentage}%
          </span>
        </div>
      </div>

      {/* Line-by-line comparison */}
      <div className="p-4 space-y-2">
        {lineResults && lineResults.map((lr, i) => (
          <div key={i} className="flex gap-4 text-sm font-mono">
            {/* Expected */}
            <div className="flex-1">
              {i === 0 && <div className="text-xs text-gray-500 mb-1 font-sans">Expected:</div>}
              <div className="text-green-400 bg-green-900/20 px-2 py-1 rounded">
                {lr.expected}
              </div>
            </div>
            {/* User's answer */}
            <div className="flex-1">
              {i === 0 && <div className="text-xs text-gray-500 mb-1 font-sans">Your answer:</div>}
              <div className={`px-2 py-1 rounded ${
                lr.score === 1 ? 'text-green-400 bg-green-900/20' :
                lr.score === 0.5 ? 'text-yellow-400 bg-yellow-900/20' :
                'text-red-400 bg-red-900/20'
              }`}>
                {lr.userLine || '(missing)'}
              </div>
            </div>
            {/* Score indicator */}
            <div className="w-8 flex items-center justify-center">
              {lr.score === 1 ? '✓' : lr.score === 0.5 ? '~' : '✗'}
            </div>
          </div>
        ))}
      </div>

      {/* Common mistake callouts */}
      {matchedMistakes && matchedMistakes.length > 0 && (
        <div className="px-4 pb-4">
          {matchedMistakes.map((mistake, i) => (
            <div key={i} className="bg-orange-900/30 border border-orange-800/50 rounded-lg p-3 mt-2">
              <div className="text-xs text-orange-400 font-medium mb-1">Common Mistake</div>
              <div className="text-sm text-orange-200">{mistake.feedback}</div>
            </div>
          ))}
        </div>
      )}

      {/* Explanation */}
      {acceptedAnswer?.explanation && (
        <div className="px-4 pb-4 border-t border-gray-800 pt-3">
          <div className="text-xs text-gray-500 mb-1">Explanation:</div>
          <div className="text-sm text-gray-300">{acceptedAnswer.explanation}</div>
        </div>
      )}
    </div>
  );
}
